import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { socialMediaApi } from '../api';
import { socialMediaQueryKeys } from '../api/queryKeys';

export type BrandSettingsData = Awaited<
  ReturnType<typeof socialMediaApi.getBrandSettings>
>['settings'];

export interface UseBrandSettingsResult {
  settings: BrandSettingsData | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadSettings: () => Promise<void>;
  saveSettings: (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[1]) => Promise<void>;
}

/**
 * Holder-scoped brand settings. Stays in a no-op loading state when
 * no Holder is selected so the caller doesn't have to gate the render
 * tree separately.
 */
export function useBrandSettings(holderId: string | null): UseBrandSettingsResult {
  const queryClient = useQueryClient();
  const key = socialMediaQueryKeys.brandSettings(holderId ?? '');

  const query = useQuery({
    queryKey: key,
    queryFn: () => socialMediaApi.getBrandSettings(holderId as string),
    enabled: !!holderId,
  });

  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[1]) => {
      if (!holderId) {
        throw new Error('No Holder selected — cannot save brand settings');
      }
      return socialMediaApi.upsertBrandSettings(holderId, data);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(key, result);
    },
  });

  // Stable references (the query/mutation result objects are new each render).
  const { refetch } = query;
  const { mutateAsync: upsert } = mutation;

  const loadSettings = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const saveSettings = useCallback(
    async (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[1]) => {
      await upsert(data);
    },
    [upsert],
  );

  const error =
    query.error instanceof Error
      ? query.error.message
      : mutation.error instanceof Error
        ? mutation.error.message
        : null;

  return {
    settings: query.data?.settings ?? null,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    error,
    loadSettings,
    saveSettings,
  };
}
